/**
 * Auth feature module — setAuth/authFailed/signinSucceeded/useStandaloneAuth verbs,
 * the pending-token-refresh request state (piggyback semantics), its outbound
 * sender, and the proactive-refresh scheduling + canonical-display-name helpers.
 * Case bodies, senders, module state, and helpers moved verbatim from
 * embeddedBridge.ts.
 */

import type { HandlerMap } from '../dispatch';
import { enqueueSendToNative } from '../transport';
import { useAccountStore } from '@/store/accountStore.ts';
import { trackEvent } from '@/telemetry/trackEvent';
import { env } from '@/config/environment';

// ─── Message types ───────────────────────────────────────────────────────

export interface SetAuthMessage {
  type: 'setAuth';
  payload: { accessToken: string; userId: string; email?: string; displayName?: string };
}

export interface AuthFailedMessage {
  type: 'authFailed';
  payload: Record<string, never>;
}

export interface UseStandaloneAuthMessage {
  type: 'useStandaloneAuth';
  payload: Record<string, never>;
}

export interface SigninSucceededMessage {
  type: 'signinSucceeded';
  payload: { provider: string; isNewUser: boolean };
}

export interface ClearAuthMessage {
  type: 'clearAuth';
  payload: Record<string, never>;
}

export type AuthMessage =
  | SetAuthMessage
  | AuthFailedMessage
  | UseStandaloneAuthMessage
  | SigninSucceededMessage
  | ClearAuthMessage;

// ─── Token refresh state ─────────────────────────────────────────────────

let pendingRefresh: {
  callbacks: Array<(ok: boolean) => void>;
  timeout: ReturnType<typeof setTimeout>;
} | null = null;

let proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null;

/** Decode JWT exp claim without a library. */
function getJwtExp(jwt: string): number | null {
  try {
    const parts = jwt.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch { return null; }
}

/**
 * Fetch the user's canonical display name from Supabase user profile.
 * The JWT may have stale/missing metadata (Apple only sends name on first sign-in),
 * so we hit the auth endpoint to get the same name the web version uses.
 * Only updates if a better name is found.
 */
function fetchCanonicalDisplayName(accessToken: string, currentName: string): void {
  const url = env.supabaseUrl;
  const anonKey = env.supabaseAnonKey;
  if (!url || !anonKey) return;

  fetch(`${url}/auth/v1/user`, {
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'apikey': anonKey,
    },
  })
    .then(r => r.ok ? r.json() : null)
    .then(user => {
      if (!user) return;
      const meta = user.user_metadata ?? {};
      const name =
        (typeof meta.full_name === 'string' && meta.full_name.trim()) ||
        (typeof meta.name === 'string' && meta.name.trim()) ||
        null;
      if (name && name !== currentName) {
        useAccountStore.getState().setAccountUser({
          ...useAccountStore.getState().accountUser!,
          displayName: name,
        });
        import('@/store/commentStore.ts').then(({ useCommentStore }) => {
          useCommentStore.getState().setReviewerName(name);
        });
      }
    })
    .catch(() => { /* best effort — JWT name is the fallback */ });
}

/** Schedule a proactive token refresh ~5 minutes before JWT expiry. */
function scheduleProactiveRefresh(accessToken: string): void {
  if (proactiveRefreshTimer != null) {
    clearTimeout(proactiveRefreshTimer);
    proactiveRefreshTimer = null;
  }

  const exp = getJwtExp(accessToken);
  if (exp == null) return;

  const msUntilExpiry = exp * 1000 - Date.now();
  // Refresh 5 minutes early, but at least 10 seconds from now
  const refreshIn = Math.max(msUntilExpiry - 5 * 60 * 1000, 10_000);

  proactiveRefreshTimer = setTimeout(() => {
    proactiveRefreshTimer = null;
    requestTokenRefresh().catch(() => { /* proactive — best effort */ });
  }, refreshIn);
}

// ─── Inbound handlers ────────────────────────────────────────────────────

export const authHandlers = {
  setAuth: (msg: SetAuthMessage) => {
    // Extract email + display name from the Supabase JWT payload
    let email = '';
    let displayName = 'User';
    try {
      const parts = msg.payload.accessToken.split('.');
      if (parts.length === 3) {
        const jwt = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
        if (typeof jwt.email === 'string') email = jwt.email;
        const meta = jwt.user_metadata ?? {};
        if (typeof meta.full_name === 'string' && meta.full_name.trim()) {
          displayName = meta.full_name.trim();
        } else if (typeof meta.name === 'string' && meta.name.trim()) {
          displayName = meta.name.trim();
        } else if (email) {
          // Apple "Hide My Email" relay addresses have gibberish prefixes — use "Creator" instead
          const isAppleRelay = email.endsWith('@privaterelay.appleid.com');
          displayName = isAppleRelay ? 'Creator' : (email.split('@')[0] || 'User');
        }
      }
    } catch { /* JWT decode failed — fall back to defaults */ }
    const resolvedName = msg.payload.displayName ?? displayName;
    useAccountStore.getState().setAccountUser({
      id: msg.payload.userId,
      email: msg.payload.email ?? email,
      isAnonymous: false,
      displayName: resolvedName,
    });
    // Set reviewer name immediately so comments authored before Share have the right name
    import('@/store/commentStore.ts').then(({ useCommentStore }) => {
      useCommentStore.getState().setReviewerName(resolvedName);
    });
    useAccountStore.getState().setNativeAccessToken(msg.payload.accessToken);
    // Fetch credit balance now that we have auth
    useAccountStore.getState().refreshBalance();
    // Resolve any pending refresh request
    if (pendingRefresh) {
      clearTimeout(pendingRefresh.timeout);
      for (const cb of pendingRefresh.callbacks) cb(true);
      pendingRefresh = null;
    }
    // Schedule proactive refresh before this token expires
    scheduleProactiveRefresh(msg.payload.accessToken);
    // Update Realtime auth on the embedded Supabase client (if active)
    import('@/api/supabaseClient.ts').then(m => {
      const sb = m.getEmbeddedSupabaseSync();
      if (sb) sb.realtime.setAuth(msg.payload.accessToken);
    });
    // Fetch canonical display name from Supabase user profile —
    // JWT metadata may be stale or missing (Apple only sends name on first sign-in).
    // This ensures embedded uses the same name as the web version.
    fetchCanonicalDisplayName(msg.payload.accessToken, resolvedName);
  },
  authFailed: (_msg: AuthFailedMessage) => {
    if (pendingRefresh) {
      clearTimeout(pendingRefresh.timeout);
      for (const cb of pendingRefresh.callbacks) cb(false);
      pendingRefresh = null;
    }
  },
  signinSucceeded: (msg: SigninSucceededMessage) => {
    trackEvent('signin_succeeded', {
      provider: msg.payload.provider,
      is_new_user: msg.payload.isNewUser,
    });
  },
  clearAuth: (_msg: ClearAuthMessage) => {
    // Unity signed out (logout, Apple revocation, definitive refresh rejection). Drop the
    // user + token so no stale-token AI call is attempted. Unity presents the forced
    // sign-in surface natively; embedded UI renders no sign-in affordance of its own.
    useAccountStore.getState().setAccountUser(null);
    useAccountStore.getState().setNativeAccessToken(null);
    useAccountStore.getState().setAccountBalance(null);
  },
  useStandaloneAuth: (_msg: UseStandaloneAuthMessage) => {
    // Editor standalone auth: Unity can't do Apple Sign In, so the web app
    // uses its own browser Supabase session for auth, edge calls, and Realtime.
    useAccountStore.getState().setStandaloneAuthMode(true);
    import('@/api/supabaseClient.ts').then(async ({ getSupabase }) => {
      const sb = await getSupabase();
      if (!sb) return;
      // Recover existing browser session (persisted from previous WebView open)
      try {
        const { data } = await sb.auth.getSession();
        if (data.session) {
          const user = data.session.user;
          const meta = user.user_metadata ?? {};
          const displayName =
            (typeof meta.full_name === 'string' && meta.full_name.trim()) ||
            (typeof meta.name === 'string' && meta.name.trim()) ||
            (user.email ? user.email.split('@')[0] : 'User');
          useAccountStore.getState().setAccountUser({
            id: user.id,
            email: user.email ?? '',
            isAnonymous: false,
            displayName,
          });
          useAccountStore.getState().refreshBalance();
          // Set reviewer name for comments
          import('@/store/commentStore.ts').then(({ useCommentStore }) => {
            useCommentStore.getState().setReviewerName(displayName);
          });
        }
      } catch {
        // No existing session — user will sign in via AuthDialog
      }
      // Listen for ongoing auth state changes (sign-in, sign-out, token refresh)
      sb.auth.onAuthStateChange((_event, session) => {
        if (session) {
          const user = session.user;
          const meta = user.user_metadata ?? {};
          const displayName =
            (typeof meta.full_name === 'string' && meta.full_name.trim()) ||
            (typeof meta.name === 'string' && meta.name.trim()) ||
            (user.email ? user.email.split('@')[0] : 'User');
          useAccountStore.getState().setAccountUser({
            id: user.id,
            email: user.email ?? '',
            isAnonymous: false,
            displayName,
          });
          useAccountStore.getState().refreshBalance();
          import('@/store/commentStore.ts').then(({ useCommentStore }) => {
            useCommentStore.getState().setReviewerName(displayName);
          });
        } else {
          useAccountStore.getState().setAccountUser(null);
          useAccountStore.getState().setAccountBalance(null);
        }
      });
    });
  },
} satisfies HandlerMap;

// ─── Token refresh ───────────────────────────────────────────────────────

/**
 * Ask Unity to refresh the Supabase access token.
 * Returns true if a fresh token was received, false on timeout or failure.
 * Deduplicates concurrent callers — only one request in flight at a time.
 */
export function requestTokenRefresh(): Promise<boolean> {
  if (pendingRefresh) {
    // Already in flight — piggyback on the existing request
    return new Promise(resolve => {
      pendingRefresh!.callbacks.push(resolve);
    });
  }

  return new Promise(resolve => {
    const timeout = setTimeout(() => {
      if (pendingRefresh) {
        for (const cb of pendingRefresh.callbacks) cb(false);
        pendingRefresh = null;
      }
    }, 10_000);

    pendingRefresh = { callbacks: [resolve], timeout };
    enqueueSendToNative('requestTokenRefresh', {});
  });
}
